import { toast } from "react-toastify"
import axios from "axios"

export const SET_IR_LOADING = "SET_IR_LOADING"
export const SET_IR_FORMS = "SET_IR_FORMS"
export const SET_TOTAL_IR_FORMS = "SET_TOTAL_IR_FORMS"
export const SET_TOTAL_IR_PAGES = "SET_TOTAL_IR_PAGES"
export const SET_IR_PRODUCTS = "SET_IR_PRODUCTS"
export const SET_IR_DATE = "SET_IR_DATE"
export const SET_IR_SUPPLIER = "SET_IR_SUPPLIER"
export const SET_IR_ETA = "SET_IR_ETA"
export const SET_IR_INTERNATIONAL = "SET_IR_INTERNATIONAL"
export const CLEAR_NEW_IR = "CLEAR_NEW_IR"

// Get Internal Requisitions
export const getIrForms = data => {
  return async (dispatch, getState) => {
    const skip = data?.skip || 1
    const limit = data?.limit || 25
    const search = (data?.search || "").trim()

    dispatch({ type: SET_IR_LOADING, data: true })

    const token = getState().auth.auth.token

    try {
      const params = new URLSearchParams({
        page: String(skip),
        limit: String(limit),
      })
      if (search) params.set("search", search)

      const res = await axios.get(
        `${
          process.env.GATSBY_API_CALL
        }/internal-requisitions?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )

      dispatch({ type: SET_IR_FORMS, data: res.data.internalRequisitions })
      dispatch({
        type: SET_TOTAL_IR_FORMS,
        data: res.data.totalInternalRequisitions,
      })
      dispatch({ type: SET_TOTAL_IR_PAGES, data: res.data.totalPages })
      dispatch({ type: SET_IR_LOADING, data: false })
    } catch (e) {
      console.log(e)
      toast.error(
        `Error: ${e.response?.data.error || "Failed to fetch IR forms"}`
      )
    } finally {
      dispatch({ type: SET_IR_LOADING, data: false })
    }
  }
}

// Add new IR form
export const addIrForm = () => {
  return async (dispatch, getState) => {
    const token = getState().auth.auth.token
    const irData = getState().internalRequisition.newIr
    const currentUser = getState().auth.auth.user

    if (!irData.irProducts || irData.irProducts.length === 0) {
      toast.error("Please add at least one item to the IR form")
      return
    }
    if (!irData.date) {
      toast.error("Please fill in the date")
      return
    }

    const newIrData = {
      date: irData.date,
      supplierCode: irData.supplier,
      eta: irData.eta,
      international: irData.international,
      products: irData.irProducts.map(p => ({
        itemCode: p.itemCode ?? p.sku,
        description: p.description ?? p.productName,
        orderQty: Number(p.orderQty || 1),
        availableQty: String(p.availableQty ?? p.stock ?? ""),
        moq: Number(p.moq || 1),
      })),
      preparedBy: { _id: currentUser._id, name: currentUser.name },
    }

    dispatch({ type: SET_IR_LOADING, data: true })

    try {
      const res = await axios.post(
        `${process.env.GATSBY_API_CALL}/internal-requisitions`,
        newIrData,
        { headers: { Authorization: `Bearer ${token}` } }
      )

      const existing = getState().internalRequisition.irForms || []
      const newIr = res.data
      const updatedIrList = [newIr, ...existing]

      dispatch({ type: SET_IR_FORMS, data: updatedIrList })

      toast.success("IR form created successfully!")
      dispatch({ type: CLEAR_NEW_IR })
      return newIr
    } catch (e) {
      console.log(e)
      toast.error(
        `Error: ${e.response?.data.error || "Failed to create IR form"}`
      )
      dispatch({ type: SET_IR_LOADING, data: false })
    }
  }
}

// Cancel Internal Requisition
export const cancelIrForm = (id, reason) => {
  return async (dispatch, getState) => {
    const token = getState().auth.auth.token

    try {
      const res = await axios.patch(
        `${process.env.GATSBY_API_CALL}/internal-requisitions/cancel/${id}`,
        { reason },
        { headers: { Authorization: `Bearer ${token}` } }
      )

      // Update the IR form in the list
      const updatedIrForms = getState().internalRequisition.irForms.map(ir =>
        ir._id === id ? res.data : ir
      )

      dispatch({
        type: SET_IR_FORMS,
        data: updatedIrForms,
      })

      toast.success("Internal requisition cancelled successfully!")
      return res.data
    } catch (e) {
      console.log(e)
      toast.error(
        `Error: ${
          e.response?.data.error || "Failed to cancel internal requisition"
        }`
      )
      throw e
    }
  }
}

// Edit ETA
export const editIrEta = (id, eta) => {
  return async (dispatch, getState) => {
    const token = getState().auth.auth.token

    try {
      const res = await axios.patch(
        `${process.env.GATSBY_API_CALL}/internal-requisitions/editETA/${id}`,
        { eta },
        { headers: { Authorization: `Bearer ${token}` } }
      )

      const updatedIrForms = getState().internalRequisition.irForms.map(ir =>
        ir._id === id ? res.data : ir
      )

      dispatch({
        type: SET_IR_FORMS,
        data: updatedIrForms,
      })

      toast.success("ETA updated successfully!")
      return res.data
    } catch (e) {
      console.log(e)
      toast.error(`Error: ${e.response?.data.error || "Failed to update ETA"}`)
      throw e
    }
  }
}

// Delete IR form
export const deleteIrForm = id => {
  return async (dispatch, getState) => {
    const token = getState().auth.auth.token

    if (!window.confirm("Are you sure you want to delete this IR form?")) {
      return
    }

    dispatch({
      type: SET_IR_LOADING,
      data: true,
    })

    try {
      await axios.delete(
        `${process.env.GATSBY_API_CALL}/internal-requisitions/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      toast.success("IR form deleted successfully!")

      dispatch(getIrForms())
    } catch (e) {
      console.log(e)
      toast.error(
        `Error: ${e.response?.data.error || "Failed to delete IR form"}`
      )
      dispatch({
        type: SET_IR_LOADING,
        data: false,
      })
    }
  }
}

// Action creators for form management
export const setIrDate = date => ({
  type: SET_IR_DATE,
  data: date,
})

export const setIrSupplier = supplierCode => ({
  type: SET_IR_SUPPLIER,
  data: supplierCode,
})

export const setIrEta = eta => ({
  type: SET_IR_ETA,
  data: eta,
})

export const setIrInternational = international => ({
  type: SET_IR_INTERNATIONAL,
  data: international,
})

export const clearNewIr = () => ({
  type: CLEAR_NEW_IR,
})

export const deleteIrProduct = productId => {
  return (dispatch, getState) => {
    const newIrProducts = getState().internalRequisition.newIr.irProducts.filter(
      prod => prod.id !== productId
    )

    dispatch({
      type: SET_IR_PRODUCTS,
      data: newIrProducts,
    })
  }
}

export const setIrProductQuantity = (productId, quantity) => {
  return (dispatch, getState) => {
    const newIrProducts = getState().internalRequisition.newIr.irProducts.map(
      prod => {
        if (prod.id === productId) {
          return { ...prod, orderQty: quantity }
        } else {
          return prod
        }
      }
    )

    dispatch({
      type: SET_IR_PRODUCTS,
      data: newIrProducts,
    })
  }
}

// Add product to IR form
export const addIrProduct = productId => {
  return (dispatch, getState) => {
    const irProductsFromState =
      getState().internalRequisition.newIr.irProducts || []

    const itemExists = irProductsFromState.some(prod => prod.id === productId)

    if (itemExists) {
      toast.error(`Error. Item has already been added.`)
      return
    }

    const productData = getState().product.products.find(
      prod => prod.id === productId
    )

    if (!productData) {
      toast.error("Product not found")
      return
    }

    const newIrProduct = {
      ...productData,
      itemCode: productData.sku,
      description: productData.productName,
      orderQty: 1,
      availableQty: productData.stock || 0,
      moq: productData.minStock,
    }

    const newIrProducts = [...irProductsFromState, newIrProduct]

    dispatch({
      type: SET_IR_PRODUCTS,
      data: newIrProducts,
    })
  }
}
